import { useEffect, useState, type ReactNode } from 'react';
import { deleteMantenimiento, getMantenimientosPaginados } from '../../lib';
import type { Mantenimiento } from '../../lib/appTypes';
import { SectionSurface } from '../../components/role-ui';
import FormMantenimiento from './FormMantenimiento';
import { Modal, confirmar, SkeletonRows } from '../../components/ui-kit';

function PanelPopup({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-5xl">
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

function formatDate(value?: string): string {
  if (!value) return '—';

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('es-EC', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(parsed);
}

function vehiculoLabel(mantenimiento: Mantenimiento): string {
  const partes = [
    mantenimiento.vehiculoPlaca,
    [mantenimiento.vehiculoMarca, mantenimiento.vehiculoModelo].filter(Boolean).join(' '),
  ].filter(Boolean);

  return partes.length > 0 ? partes.join(' · ') : `Vehículo ${mantenimiento.vehiculo}`;
}

function responsableLabel(mantenimiento: Mantenimiento): string {
  return (
    mantenimiento.responsableDisplay ||
    mantenimiento.responsableNombre ||
    mantenimiento.conductorResponsableNombre ||
    '—'
  );
}

export default function MantenimientosPanel() {
  const [mantenimientos, setMantenimientos] = useState<Mantenimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMantenimiento, setEditingMantenimiento] = useState<Mantenimiento | null>(null);

  // Paginación servidor
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  async function refresh(p = page) {
    try {
      setLoading(true);
      const result = await getMantenimientosPaginados({
        page: p,
        page_size: pageSize,
        search: busqueda.trim() || undefined,
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
      });
      setMantenimientos(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(result.page);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los mantenimientos.');
    } finally {
      setLoading(false);
    }
  }

  // Totales para el card (sin paginar)
  const [totalMant, setTotalMant] = useState(0);

  async function loadTotalCount() {
    try {
      const r = await getMantenimientosPaginados({ page: 1, page_size: 1 });
      setTotalMant(r.count);
    } catch { /*silencioso*/ }
  }

  useEffect(() => {
    void refresh(1);
    void loadTotalCount();
  }, []);

  function handleSearch() {
    setPage(1);
    void refresh(1);
  }

  async function handleLimpiarFiltros() {
    setBusqueda('');
    setFechaDesde('');
    setFechaHasta('');
    setPage(1);
    try {
      setLoading(true);
      const result = await getMantenimientosPaginados({ page: 1, page_size: pageSize });
      setMantenimientos(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(1);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los mantenimientos.');
    } finally {
      setLoading(false);
    }
  }

  function goToPage(p: number) {
    setPage(p);
    void refresh(p);
  }

  async function handleRefreshAfterAction() {
    await loadTotalCount();
    await refresh(page);
  }

  async function handleEliminarMantenimiento(mantenimiento: Mantenimiento) {
    if (!(await confirmar({ titulo: `¿Eliminar el mantenimiento de ${vehiculoLabel(mantenimiento)}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;
    try {
      await deleteMantenimiento(mantenimiento.id);
      await handleRefreshAfterAction();
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar el mantenimiento.');
    }
  }

  const inputCls = 'w-full rounded-[14px] border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-stone-400 focus:ring-4 focus:ring-stone-100';

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          <button
            type="button"
            onClick={() => {
              setEditingMantenimiento(null);
              setShowForm(true);
            }}
            className="ui-btn ui-btn-primary"
          >
            Nuevo mantenimiento
          </button>
        </div>

        {dataError && (
          <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {dataError}
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-1">
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total mantenimientos</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{totalMant}</p>
          </article>
        </div>

        <SectionSurface className="space-y-5">
          <div className="flex flex-col gap-3">
            <div>
              <h4 className="text-lg font-semibold text-stone-900">Historial de mantenimientos</h4>
              <p className="text-sm text-stone-500">
                Filtra por placa, responsable o rango de fechas.
              </p>
            </div>

            {/* Filtros */}
            <div className="ui-filtros">
              <div className="ui-filtro-busqueda">
                <label className="ui-filtro-label">
                  Buscar por placa o responsable
                </label>
                <input
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Placa, responsable, descripción..."
                  className={inputCls}
                />
              </div>
              <div>
                <label className="ui-filtro-label">
                  Fecha desde
                </label>
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={e => setFechaDesde(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="ui-filtro-label">
                  Fecha hasta
                </label>
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={e => setFechaHasta(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="ui-filtro-acciones">
                <button
                  type="button"
                  onClick={handleSearch}
                  className="ui-btn ui-btn-dark"
                >
                  Buscar
                </button>
                <button
                  type="button"
                  onClick={() => void handleLimpiarFiltros()}
                  className="ui-btn ui-btn-secondary"
                >
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <SkeletonRows etiqueta="Cargando mantenimientos..." />
          ) : mantenimientos.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
              No hay mantenimientos que coincidan con los filtros.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">
                      <th className="pb-2 pr-4 font-semibold">Vehículo</th>
                      <th className="pb-2 pr-4 font-semibold">Fecha</th>
                      <th className="pb-2 pr-4 font-semibold">Salida</th>
                      <th className="pb-2 pr-4 font-semibold">Kilómetros</th>
                      <th className="pb-2 pr-4 font-semibold">Responsable</th>
                      <th className="pb-2 pr-4 font-semibold">Descripción</th>
                      <th className="pb-2 font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mantenimientos.map(mantenimiento => (
                      <tr key={mantenimiento.id} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)] align-top">
                        <td className="rounded-l-[16px] px-4 py-3">
                          <p className="font-semibold">{vehiculoLabel(mantenimiento)}</p>
                          <p className="mt-1 text-xs text-[var(--freisa-text-soft)]">
                            ID: {mantenimiento.vehiculo}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {formatDate(mantenimiento.fechaMantenimiento)}
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {formatDate(mantenimiento.fechaSalidaMantenimiento ?? undefined)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                            {new Intl.NumberFormat('es-EC').format(mantenimiento.kilometrosMantenimiento)} km
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {responsableLabel(mantenimiento)}
                        </td>
                        <td className="max-w-[360px] px-4 py-3 text-[var(--freisa-text-soft)]">
                          <p className="line-clamp-3 whitespace-pre-line">{mantenimiento.descripcion || '—'}</p>
                          {mantenimiento.urlMantenimiento.length > 0 && (
                            <div className="mt-2 flex items-center gap-1.5">
                              {mantenimiento.urlMantenimiento.slice(0, 4).map((url, indice) => (
                                <a
                                  key={url}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={`Ver imagen ${indice + 1}`}
                                  className="block h-10 w-10 overflow-hidden rounded-lg border border-stone-200 bg-white transition hover:scale-105"
                                >
                                  <img src={url} alt={`Imagen ${indice + 1}`} loading="lazy" className="h-full w-full object-cover" />
                                </a>
                              ))}
                              {mantenimiento.urlMantenimiento.length > 4 && (
                                <span className="text-xs font-semibold text-stone-500">+{mantenimiento.urlMantenimiento.length - 4}</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="rounded-r-[16px] px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingMantenimiento(mantenimiento);
                                setShowForm(false);
                              }}
                              className="ui-btn ui-btn-secondary ui-btn-sm"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleEliminarMantenimiento(mantenimiento)}
                              className="ui-btn ui-btn-danger ui-btn-sm"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-3 rounded-b-[20px]">
                <span className="text-sm text-stone-500">
                  Página {page} de {totalPages} · {totalCount} registro(s)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1 || loading}
                    className="rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages || loading}
                    className="ui-btn ui-btn-secondary ui-btn-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </SectionSurface>
      </section>

      {(showForm || editingMantenimiento) && (
        <PanelPopup
          onClose={() => {
            setShowForm(false);
            setEditingMantenimiento(null);
          }}
        >
          <FormMantenimiento
            initialMantenimiento={editingMantenimiento}
            onSaved={() => {
              setShowForm(false);
              setEditingMantenimiento(null);
              void handleRefreshAfterAction();
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingMantenimiento(null);
            }}
          />
        </PanelPopup>
      )}
    </>
  );
}
